import React, { useEffect, useState } from 'react';
import {
  Box, Typography, TextField, Paper, Grid, Button, Chip,
  FormControl, InputLabel, Select, MenuItem, Dialog, DialogTitle,
  DialogContent, DialogActions,
} from '@mui/material';
import { Search as SearchIcon, Visibility as ViewIcon } from '@mui/icons-material';
import PaginatedTable from '../components/common/PaginatedTable';
import { auditApi } from '../api/audit.api';
import LoadingSpinner from '../components/common/LoadingSpinner';

const actionColors: Record<string, 'primary' | 'success' | 'warning' | 'error' | 'info'> = {
  create: 'success',
  update: 'warning',
  delete: 'error',
  entrada: 'success',
  salida: 'info',
  ajuste: 'warning',
  ingreso_mercaderia: 'primary',
};

const AuditLogs: React.FC = () => {
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [entity, setEntity] = useState('');
  const [action, setAction] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState<any>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await auditApi.getAll({
        entity: entity || undefined,
        action: action || undefined,
        from: from || undefined,
        to: to || undefined,
      });
      setLogs(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Auditoria</Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Entidad</InputLabel>
              <Select value={entity} label="Entidad"
                onChange={(e) => setEntity(e.target.value)}>
                <MenuItem value="">Todas</MenuItem>
                <MenuItem value="inventory">Inventario</MenuItem>
                <MenuItem value="product">Productos</MenuItem>
                <MenuItem value="order">Pedidos</MenuItem>
                <MenuItem value="customer">Clientes</MenuItem>
                <MenuItem value="warehouse">Bodegas</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <TextField fullWidth size="small" label="Desde" type="date" value={from}
              onChange={(e) => setFrom(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }} />
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <TextField fullWidth size="small" label="Hasta" type="date" value={to}
              onChange={(e) => setTo(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }} />
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <TextField fullWidth size="small" label="Accion" value={action}
              onChange={(e) => setAction(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <Button variant="contained" startIcon={<SearchIcon />}
              onClick={load} fullWidth>
              Buscar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {loading ? <LoadingSpinner /> : (
        <PaginatedTable
          columns={[
            { id: 'id', label: 'ID', minWidth: 60 },
            {
              id: 'createdAt', label: 'Fecha',
              format: (value: string) => new Date(value).toLocaleString(),
            },
            {
              id: 'userId', label: 'Usuario',
              format: (_value: any, row: any) => row.user?.fullName || `User ${row.userId}`,
            },
            {
              id: 'entity', label: 'Entidad',
              format: (value: string) => (
                <Chip label={value} size="small" variant="outlined" />
              ),
            },
            { id: 'entityId', label: 'ID Entidad' },
            {
              id: 'action', label: 'Accion',
              format: (value: string) => (
                <Chip label={value} size="small"
                  color={actionColors[value] || 'default'} />
              ),
            },
          ]}
          rows={logs}
          searchFields={['entity', 'action', 'entityId']}
          exportFileName="auditoria"
          emptyMessage="No hay registros de auditoria"
          renderActions={(log) => (
            <Button size="small" startIcon={<ViewIcon />}
              onClick={() => { setSelectedLog(log); setDetailOpen(true); }}>
              Ver
            </Button>
          )}
        />
      )}

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Detalle de Auditoria #{selectedLog?.id}</DialogTitle>
        <DialogContent>
          {selectedLog && (
            <Box>
              <Typography variant="body2" gutterBottom>
                <strong>Fecha:</strong> {new Date(selectedLog.createdAt).toLocaleString()}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Usuario:</strong> {selectedLog.user?.fullName}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Entidad:</strong> {selectedLog.entity} #{selectedLog.entityId}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Accion:</strong> {selectedLog.action}
              </Typography>
              {selectedLog.oldValues && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="error.main">Valores anteriores:</Typography>
                  <Paper variant="outlined" sx={{ p: 1, mt: 0.5 }}>
                    <pre style={{ margin: 0, fontSize: '0.8rem', overflow: 'auto' }}>
                      {JSON.stringify(selectedLog.oldValues, null, 2)}
                    </pre>
                  </Paper>
                </Box>
              )}
              {selectedLog.newValues && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="success.main">Valores nuevos:</Typography>
                  <Paper variant="outlined" sx={{ p: 1, mt: 0.5 }}>
                    <pre style={{ margin: 0, fontSize: '0.8rem', overflow: 'auto' }}>
                      {JSON.stringify(selectedLog.newValues, null, 2)}
                    </pre>
                  </Paper>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuditLogs;
